import { useCallback, useEffect, useMemo, useRef } from 'react';
import type MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import HeatmapRenderer from '@arcgis/core/renderers/HeatmapRenderer';

/**
 * Renders a density heatmap from consumer-supplied points.
 *
 * Why this exists: a consumer cannot build the ArcGIS objects itself. Host and
 * remote are separate builds, so each resolves `@arcgis/core` to its own copy
 * and `Map.add()` rejects a layer whose class came from the other one. The only
 * safe boundary is plain data in, nothing ArcGIS out — which is exactly why the
 * Mapbox heatmap path has always worked.
 *
 * Why FeatureLayer and not GeoJSONLayer: `GeoJSONLayer` parses its features at
 * construction and cannot be mutated afterwards, so refreshing it means
 * removing the layer and building a new one. Measured against a 1500ms
 * animation that blanks the map on every tick — median 26-43ms, over a second
 * on the first — which reads as a flicker. A `FeatureLayer` with a client-side
 * `source` accepts `applyEdits`, so the same layer instance survives and the
 * deletes and adds land in one atomic edit with no empty state between them.
 *
 * `GraphicsLayer` is not an option either: it takes no renderer, which is what
 * the previous implementation traded away to stop the flicker.
 */

/** A stop on the density colour ramp. `ratio` runs 0-1 against `maxDensity`. */
export type PointHeatmapColorStop = {
	ratio: number;
	color: string;
};

export type PointHeatmapOptions = {
	/**
	 * Density colour ramp. The lowest stop should be fully transparent or the
	 * basemap will be hidden where density is near zero.
	 */
	colorStops?: PointHeatmapColorStop[];
	/** Kernel search radius in points. Esri's default is 18. */
	radius?: number;
	/** Densities below this are not drawn. */
	minDensity?: number;
	/**
	 * Density that maps to the hottest colour stop. This is a **divisor**, so
	 * the direction is counter-intuitive and easy to get wrong: raising it makes
	 * the surface fainter, and a value well above your actual peak density maps
	 * everything to ratio ~0 — which is transparent — so nothing renders at all.
	 * Lower it to make the surface hotter.
	 *
	 * There is no safe default, because density depends on `radius` and on how
	 * closely your points sit. Esri's own default is 0.04. Tune against real
	 * data: start there, and if the surface is invisible, divide it down.
	 */
	maxDensity?: number;
	/** Layer opacity, 0-1. */
	opacity?: number;
	/**
	 * Freeze the surface at a scale so it stops rescaling as the user zooms.
	 * Pass `view.scale` to lock it to the current view.
	 */
	referenceScale?: number;
};

/** `[lng, lat]`, or `[lng, lat, weight]`. Weight defaults to 1. */
export type PointHeatmapPoint = [number, number] | [number, number, number];

/**
 * Imperative handle for a point heatmap. Takes plain numbers only, so it is
 * safe to call across a Module Federation boundary.
 */
export type PointHeatmapOverlay = {
	/** Replace the surface's points. Safe to call before the map is ready. */
	set(points: PointHeatmapPoint[], options?: PointHeatmapOptions): void;
	/** Remove every point, leaving the layer in place. */
	clear(): void;
};

const DEFAULT_POINT_HEATMAP_LAYER_ID = 'point-heatmap';

/** Esri's own default layer opacity, restored when a consumer omits it. */
const DEFAULT_OPACITY = 1;
const OBJECT_ID_FIELD = 'OBJECTID';
const WEIGHT_FIELD = 'weight';

/**
 * Esri requires the lowest stop to be fully transparent or the basemap is
 * hidden everywhere. The stops above it climb to a visible alpha quickly, so
 * that an under-tuned `maxDensity` still shows a faint surface rather than
 * nothing at all — a silent blank map is the worst failure mode here.
 *
 * Cool-to-hot rather than warm-only on purpose: the Info360 basemaps are pale
 * yellows and greens, and a yellow-to-red ramp is close to invisible on them
 * until density is already high.
 */
const DEFAULT_COLOR_STOPS: PointHeatmapColorStop[] = [
	{ ratio: 0, color: 'rgba(0, 80, 255, 0)' },
	{ ratio: 0.08, color: 'rgba(0, 120, 255, 0.6)' },
	{ ratio: 0.35, color: 'rgba(0, 220, 160, 0.8)' },
	{ ratio: 0.65, color: 'rgba(255, 210, 0, 0.9)' },
	{ ratio: 1, color: 'rgba(220, 0, 20, 1)' },
];

function buildRenderer(options?: PointHeatmapOptions): HeatmapRenderer {
	return new HeatmapRenderer({
		field: WEIGHT_FIELD,
		colorStops: options?.colorStops ?? DEFAULT_COLOR_STOPS,
		...(options?.radius != null ? { radius: options.radius } : {}),
		...(options?.minDensity != null
			? { minDensity: options.minDensity }
			: {}),
		...(options?.maxDensity != null
			? { maxDensity: options.maxDensity }
			: {}),
		...(options?.referenceScale != null
			? { referenceScale: options.referenceScale }
			: {}),
	});
}

export type UseEsriPointHeatmapOptions = {
	/** Distinct layer id when more than one heatmap is on the map. */
	layerId?: string;
};

/**
 * Owns one client-side `FeatureLayer` and returns a handle for replacing its
 * points. Pass the `MapView` rather than reading context, so this can be called
 * from the component that forwards the ref.
 */
const useEsriPointHeatmap = (
	view: MapView | null,
	options?: UseEsriPointHeatmapOptions,
): PointHeatmapOverlay => {
	const layerId = options?.layerId ?? DEFAULT_POINT_HEATMAP_LAYER_ID;

	const layerRef = useRef<FeatureLayer | null>(null);
	/** Object ids currently on the layer, so the next edit can delete them. */
	const liveIdsRef = useRef<number[]>([]);
	/** Set before the view was ready; applied once the layer exists. */
	const pendingRef = useRef<{
		points: PointHeatmapPoint[];
		options?: PointHeatmapOptions;
	} | null>(null);
	/** Serialises edits so overlapping ticks cannot interleave. */
	const editChainRef = useRef<Promise<unknown>>(Promise.resolve());
	/** Last applied options, to avoid rebuilding the renderer every tick. */
	const appliedOptionsRef = useRef<string | null>(null);

	const applyOptions = useCallback(
		(layer: FeatureLayer, next?: PointHeatmapOptions) => {
			const key = JSON.stringify(next ?? {});
			if (key === appliedOptionsRef.current) return;
			appliedOptionsRef.current = key;
			layer.renderer = buildRenderer(next);
			// Reset rather than leave the last value in place: a consumer that
			// stops passing `opacity` means the default, not "keep whatever it
			// was set to last time".
			layer.opacity = next?.opacity ?? DEFAULT_OPACITY;
		},
		[],
	);

	const writePoints = useCallback(
		(
			layer: FeatureLayer,
			points: PointHeatmapPoint[],
			next?: PointHeatmapOptions,
		) => {
			applyOptions(layer, next);

			// A client-side FeatureLayer assigns its own object ids, so the new
			// features must not carry one — the ids come back in the edit result
			// and are what the *next* call deletes.
			const addFeatures = points.map(
				([lng, lat, weight]) =>
					new Graphic({
						geometry: new Point({ longitude: lng, latitude: lat }),
						attributes: { [WEIGHT_FIELD]: weight ?? 1 },
					}),
			);

			// Queued rather than run inline: the ids to delete are only known once
			// the previous edit has resolved, so each tick reads them at run time.
			editChainRef.current = editChainRef.current
				.then(async () => {
					const deleteFeatures = liveIdsRef.current.map(objectId => ({
						objectId,
					}));

					// One applyEdits call, so the deletes and adds are a single
					// atomic edit and the layer is never briefly empty. Two calls
					// would flicker.
					const result = await layer.applyEdits({
						addFeatures,
						deleteFeatures,
					});

					liveIdsRef.current = (result.addFeatureResults ?? [])
						.map(r => r.objectId)
						.filter((id): id is number => typeof id === 'number');
				})
				.catch((error: unknown) => {
					// Swallowing would make an empty heatmap impossible to explain.
					console.error(
						'[useEsriPointHeatmap] applyEdits failed',
						error,
					);
				});
		},
		[applyOptions],
	);

	useEffect(() => {
		const esriMap = view?.map;
		if (!esriMap) return;

		const layer = new FeatureLayer({
			id: layerId,
			source: [],
			objectIdField: OBJECT_ID_FIELD,
			geometryType: 'point',
			spatialReference: { wkid: 4326 },
			fields: [
				{ name: OBJECT_ID_FIELD, type: 'oid' },
				{ name: WEIGHT_FIELD, type: 'double' },
			],
			renderer: buildRenderer(pendingRef.current?.options),
			opacity: pendingRef.current?.options?.opacity ?? DEFAULT_OPACITY,
		});

		appliedOptionsRef.current = JSON.stringify(
			pendingRef.current?.options ?? {},
		);

		esriMap.add(layer);
		layerRef.current = layer;
		liveIdsRef.current = [];

		// Asset layers added after this one would otherwise draw on top.
		esriMap.reorder(layer, esriMap.layers.length - 1);

		const pending = pendingRef.current;
		if (pending) {
			writePoints(layer, pending.points, pending.options);
			pendingRef.current = null;
		}

		return () => {
			esriMap.remove(layer);
			layerRef.current = null;
			liveIdsRef.current = [];
			appliedOptionsRef.current = null;
		};
	}, [view, layerId, writePoints]);

	return useMemo<PointHeatmapOverlay>(
		() => ({
			set(points, next) {
				const layer = layerRef.current;
				if (!layer) {
					// View not ready — remember the latest call and apply it on create.
					pendingRef.current = { points, options: next };
					return;
				}
				writePoints(layer, points, next);
			},
			clear() {
				pendingRef.current = null;
				const layer = layerRef.current;
				if (!layer) return;
				editChainRef.current = editChainRef.current
					.then(async () => {
						const deleteFeatures = liveIdsRef.current.map(
							objectId => ({ objectId }),
						);
						if (deleteFeatures.length === 0) return;
						// Only forget the ids once the delete has landed. Zeroing
						// first would orphan those features on the layer with no
						// way to remove them if applyEdits rejects.
						await layer.applyEdits({ deleteFeatures });
						liveIdsRef.current = [];
					})
					.catch((error: unknown) => {
						console.error(
							'[useEsriPointHeatmap] applyEdits failed',
							error,
						);
					});
			},
		}),
		[writePoints],
	);
};

export default useEsriPointHeatmap;
